// The small pieces every award surface is built from (AWARDS.md): the tone
// colors, the award card, the trophy tile, the badge, and the share sheet.
// Screens compose these; none of them fetch.
//
// Drawn through the kit (LOOKS.md Part 7). An award's glyph and the cup are
// ART, the look's to draw (`kit/Icon` AwardGlyph, `kit/art` Cup); a team's
// color reaches a card as data (`Colored`), never as a style of its own.

import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import type { MilestoneView, AwardCardView, AwardTier, TeamRef, TrophyView } from '@fsp/domain';
import { LiveDot, TeamMark } from '../../kit';
import { Cup } from '../../kit/art';
import { AwardGlyph, Icon } from '../../kit/Icon';
import {
  Button,
  Caption,
  Colored,
  Cover,
  Line,
  ListRow,
  Meter,
  RowName,
  RowSub,
  Stack,
  Text,
  tell,
} from '../../kit/ui';
import { DetailSheet } from '../../templates/DetailSheet';
import { TeamName } from './Names';
import {
  canShareFiles,
  drawClassPhoto,
  drawShareCard,
  downloadCanvas,
  shareCard,
  type ClassPhotoInput,
  type ShareCardInput,
} from '../../share/shareCard';

export { Cup };

/** An award's glyph, as the look draws it. The name every award surface already uses. */
export const AwardIcon = AwardGlyph;

export const TIER_LABEL: Record<AwardTier, string> = {
  bronze: 'Bronze',
  silver: 'Silver',
  gold: 'Gold',
  platinum: 'Platinum',
};

/** The winner's line on a card: mark, team, owner. */
export function WinnerLine({ team, size = 22 }: { team: TeamRef; size?: number }) {
  return (
    <span className="fsp-award-winner">
      <TeamMark team={team} size={size} />
      <span className="fsp-award-winner-name">
        <TeamName team={team} />
      </span>
      <span className="fsp-sub">{team.ownerName}</span>
      {team.titles ? <TitleCrown titles={team.titles} /> : null}
    </span>
  );
}

/** A small crown with the count, next to any team name that has titles. */
export function TitleCrown({ titles }: { titles: number }) {
  return (
    <span className="fsp-titles" title={`${titles} championship${titles === 1 ? '' : 's'}`}>
      <Icon name="crown" size="sm" />
      {titles > 1 ? titles : null}
    </span>
  );
}

/** One award as a card: the icon in the tone's color, the value large, the winner, the line. */
export function AwardCard({
  card,
  onOpen,
  compact = false,
}: {
  card: AwardCardView;
  onOpen?: (card: AwardCardView) => void;
  compact?: boolean;
}) {
  const winner = card.winners[0];
  const wash = winner ? winner.team.colors.primary : 'transparent';
  return (
    <Colored
      className={`fsp-surface fsp-award ${onOpen ? 'fsp-row-clickable fsp-cover-host' : ''} ${compact ? 'compact' : ''}`}
      data-tone={card.tone}
      colors={{ '--wash': wash }}
    >
      {/* THE CARD OPENS THE AWARD, THE WINNER'S NAME OPENS THE TEAM (B-220).
          The winner's corner of the card went to the trophy room, a door
          inside a door nobody could see; the room is one tap on from the
          award's own sheet and from the champion's banner. */}
      {onOpen ? <Cover label={`${card.name}: the award`} onClick={() => onOpen(card)} /> : null}
      <div className="fsp-award-head">
        <span className="fsp-award-icon">
          <AwardGlyph icon={card.icon} size={compact ? 16 : 18} />
        </span>
        <span className="fsp-award-name">{card.name}</span>
        {card.asItStands ? (
          <span className="fsp-award-standing">
            <LiveDot />
            as it stands
          </span>
        ) : null}
      </div>
      {winner ? (
        <>
          {winner.valueLabel ? (
            <div className={`fsp-award-value fsp-num fsp-disp ${compact ? 'compact' : ''}`}>
              {winner.valueLabel}
            </div>
          ) : null}
          <div className="fsp-award-winners">
            {card.winners.map((w) => (
              <WinnerLine key={w.team.id} team={w.team} size={compact ? 18 : 22} />
            ))}
          </div>
          {!compact ? <div className="fsp-award-line">{card.line}</div> : null}
        </>
      ) : (
        <div className="fsp-award-line fsp-muted">Decided in the playoffs.</div>
      )}
    </Colored>
  );
}

/** The detail sheet: the blurb, the top five, and Share. */
export function AwardDetail({
  card,
  league,
  onClose,
  onTeam,
}: {
  card: AwardCardView | null;
  league: { name: string; season: number };
  onClose: () => void;
  onTeam: (team: TeamRef) => void;
}) {
  const winner = card?.winners[0] ?? null;
  const share: ShareCardInput | null =
    card && winner
      ? {
          kicker: card.name,
          headline: winner.valueLabel || winner.team.name,
          subline: winner.valueLabel
            ? `${winner.team.name} · ${winner.team.ownerName}`
            : winner.team.ownerName,
          line: card.line,
          footer: `${league.name} · ${card.period ? `Week ${card.period}` : `${league.season} season`}`,
          team: winner.team,
          tone: card.tone,
        }
      : null;
  return (
    <DetailSheet opened={card !== null} onClose={onClose} title={card?.name ?? ''}>
      {card ? (
        <Stack gap={4}>
          <Text tone="secondary" block>
            {card.blurb}
            {card.asItStands ? ' As it stands; nothing is final until the last game ends.' : ''}
          </Text>
          <AwardCard card={card} />
          {card.standings.length > 1 ? (
            <Stack gap={2}>
              <Caption>Top five</Caption>
              <div className="fsp-surface">
                {card.standings.map((s, i) => (
                  <ListRow
                    key={s.team.id}
                    leadSize="lg"
                    lead={
                      <span className="fsp-award-rank">
                        <Text tone="muted" numeric>
                          {i + 1}
                        </Text>
                        <TeamMark team={s.team} size={22} />
                      </span>
                    }
                    trail={<Text numeric>{s.valueLabel}</Text>}
                    onClick={() => onTeam(s.team)}
                    cover={`${s.team.name}'s trophy room`}
                  >
                    <RowName>
                      <TeamName team={s.team} />
                    </RowName>
                  </ListRow>
                ))}
              </div>
            </Stack>
          ) : null}
          {share ? <SharePanel input={share} text={`${card.name}: ${card.line}`} /> : null}
        </Stack>
      ) : null}
    </DetailSheet>
  );
}

/**
 * The share panel: a preview of the drawn card and the three ways out.
 * `photo`, the yearbook's class photo in the square card's place; `input`
 * still names it and says it for a screen reader.
 */
export function SharePanel({
  input,
  text,
  photo,
}: {
  input: ShareCardInput;
  text: string;
  photo?: ClassPhotoInput;
}) {
  const [url, setUrl] = useState<string | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const key = useMemo(() => JSON.stringify([input, photo ?? null]), [input, photo]);
  useEffect(() => {
    try {
      const canvas = photo ? drawClassPhoto(photo) : drawShareCard(input);
      canvasRef.current = canvas;
      setUrl(canvas.toDataURL('image/png'));
    } catch {
      setUrl(null);
    }
    // Redraw when the card's content changes; `key` is the input, serialized.
  }, [key]);
  const title = `${input.kicker} ${input.headline}`;
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(`${text} ${location.origin}`);
      tell('Copied. Paste it in the group chat.', 'good');
    } catch {
      tell(text);
    }
  };
  return (
    <div className="fsp-share" data-tour="share">
      <Caption>Share</Caption>
      {url ? (
        <img src={url} alt={`${input.kicker}: ${input.line}`} className="fsp-share-preview" />
      ) : (
        <Text tone="secondary" block>
          The card could not be drawn here. Copy the text instead.
        </Text>
      )}
      <Line gap={2} wrap>
        {canShareFiles() && canvasRef.current ? (
          <Button
            variant="primary"
            icon="share"
            onClick={async () => {
              const ok = canvasRef.current && (await shareCard(canvasRef.current, text, title));
              if (!ok) tell('Sharing was canceled. Download works too.');
            }}
          >
            Share
          </Button>
        ) : null}
        <Button
          variant={canShareFiles() ? 'secondary' : 'primary'}
          icon="download"
          disabled={!url}
          onClick={() => canvasRef.current && downloadCanvas(canvasRef.current, title)}
        >
          Download
        </Button>
        <Button icon="copy" onClick={() => void copy()}>
          Copy text
        </Button>
      </Line>
    </div>
  );
}

/** A trophy drawn in the team's colors; the champion's is the big cup. */
export function TrophyTile({
  trophy,
  team,
  season,
  trophyName,
}: {
  trophy: TrophyView;
  team: TeamRef;
  season: number;
  trophyName: string;
}) {
  const champion = trophy.champion;
  return (
    <div
      className={`fsp-trophy ${champion ? 'champion' : ''}`}
      data-tone={trophy.tone}
      title={trophy.line}
    >
      <span className="fsp-trophy-glyph">
        {champion ? (
          <Cup primary={team.colors.primary} secondary={team.colors.secondary} size={64} />
        ) : (
          <span className="fsp-trophy-plate">
            <AwardGlyph icon={trophy.icon} size={22} />
          </span>
        )}
      </span>
      <span className="fsp-trophy-name">{champion ? trophyName : trophy.name}</span>
      <span className="fsp-sub">{champion ? `${season} champion` : String(season)}</span>
    </div>
  );
}

/** A milestone badge: lit in the tier's metal when earned, dimmed with progress when not. */
export function Badge({ a, onOpen }: { a: MilestoneView; onOpen?: (a: MilestoneView) => void }) {
  const earned = a.tier !== null;
  return (
    <div
      className={`fsp-badge ${earned ? 'earned' : 'locked'} ${onOpen ? 'fsp-row-clickable' : ''}`}
      data-tier={a.tier ?? undefined}
      data-tone={a.tone}
      onClick={onOpen ? () => onOpen(a) : undefined}
      role={onOpen ? 'button' : undefined}
      tabIndex={onOpen ? 0 : undefined}
      title={earned ? (a.earnedLine ?? a.name) : a.blurb}
    >
      <span className="fsp-badge-glyph">
        <AwardGlyph icon={a.icon} size={22} />
      </span>
      <span className="fsp-badge-name">{a.name}</span>
      <span className="fsp-sub fsp-badge-sub">
        {earned ? TIER_LABEL[a.tier!] : a.nextLabel ? a.nextLabel : a.blurb}
        {a.scope === 'career' ? ' · career' : ''}
      </span>
      {a.nextTier ? (
        <span className="fsp-badge-meter">
          <Meter value={a.progress} />
        </span>
      ) : null}
    </div>
  );
}

export function BadgeDetail({ a, onClose }: { a: MilestoneView | null; onClose: () => void }) {
  return (
    <DetailSheet opened={a !== null} onClose={onClose} title={a?.name ?? ''}>
      {a ? (
        <Stack gap={3}>
          <Text tone="secondary" block>
            {a.blurb}
            {a.scope === 'career' ? ' Counts across every season.' : ' Resets each season.'}
          </Text>
          <Badge a={a} />
          {a.earnedLine ? <Text block>{a.earnedLine}</Text> : null}
          <div className="fsp-surface">
            {a.tiers.map((t) => (
              <ListRow
                key={t.tier}
                dim={!t.earned}
                trail={t.earned ? <Caption>{t.earnedIn ?? 'Earned'}</Caption> : undefined}
              >
                <RowName>
                  <span className="fsp-badge-tier" data-tier={t.tier}>
                    {TIER_LABEL[t.tier]}
                  </span>
                </RowName>
                <RowSub>{describeThreshold(a, t.threshold)}</RowSub>
              </ListRow>
            ))}
          </div>
        </Stack>
      ) : null}
    </DetailSheet>
  );
}

function describeThreshold(a: MilestoneView, threshold: number): string {
  const value = Number.isInteger(threshold) ? String(threshold) : threshold.toFixed(1);
  return `${value}${a.valueLabel.includes('.') || !Number.isInteger(threshold) ? ' points' : ''}`;
}

export function Shelf({ title, children }: { title: ReactNode; children: ReactNode }) {
  return (
    <section className="fsp-shelf" aria-label={typeof title === 'string' ? title : undefined}>
      <div className="fsp-shelf-head">{title}</div>
      <div className="fsp-shelf-body">{children}</div>
    </section>
  );
}
